import { getSafeUserMessage } from "./errorMessages";

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_REQUIREMENTS_MESSAGE =
  "Your password must contain at least one uppercase letter, one lowercase letter, and one number.";

export const REGISTRATION_ERROR_MESSAGES = Object.freeze({
  INVALID_EMAIL: "Enter a valid email address.",
  EMAIL_REQUIRED: "Email address is required.",
  NAME_REQUIRED: "Your name is required.",
  PASSWORD_REQUIRED: "Password is required.",
  PASSWORD_TOO_SHORT: `Your password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
  PASSWORD_REQUIREMENTS: PASSWORD_REQUIREMENTS_MESSAGE,
  PASSWORDS_DO_NOT_MATCH: "The passwords do not match.",
  EMAIL_ALREADY_REGISTERED:
    "An account already exists with this email address. Sign in instead.",
  EMAIL_VERIFICATION_REQUIRED:
    "This email is already registered but still needs verification. We can send you a new verification code.",
  ACCOUNT_PENDING_VERIFICATION:
    "This email is already registered but still needs verification. We can send you a new verification code.",
  EMAIL_VERIFICATION_EXPIRED:
    "That verification link or code has expired. Request a new verification email.",
  INVALID_REGISTRATION_DATA: "Check the highlighted fields and try again.",
  VERIFICATION_EMAIL_FAILED:
    "Your account was created, but we couldn't send the verification email. Please resend the verification code.",
  EMAIL_SERVICE_UNAVAILABLE:
    "We couldn't send your verification email right now. Please try again shortly.",
  RATE_LIMITED: "Too many attempts. Please wait a moment before trying again.",
  DUPLICATE_EMAIL:
    "An account already exists with this email address. Sign in instead.",
  SERVER_ERROR:
    "We couldn't create your account because of a temporary server problem. Please try again.",
  NETWORK_ERROR:
    "We couldn't reach the server. Check your connection and try again.",
});

const FIELD_BY_CODE = Object.freeze({
  INVALID_EMAIL: "email",
  EMAIL_REQUIRED: "email",
  NAME_REQUIRED: "name",
  PASSWORD_REQUIRED: "password",
  PASSWORD_TOO_SHORT: "password",
  PASSWORD_REQUIREMENTS: "password",
  PASSWORDS_DO_NOT_MATCH: "confirmPassword",
});

const FIELD_LEVEL_CODES = new Set([
  "INVALID_EMAIL",
  "EMAIL_REQUIRED",
  "NAME_REQUIRED",
  "PASSWORD_REQUIRED",
  "PASSWORD_TOO_SHORT",
  "PASSWORD_REQUIREMENTS",
  "PASSWORDS_DO_NOT_MATCH",
  "INVALID_REGISTRATION_DATA",
]);

const DELIVERY_CODES = new Set([
  "VERIFICATION_EMAIL_FAILED",
  "EMAIL_SERVICE_UNAVAILABLE",
]);

const SAFE_FIELDS = new Set([
  "title",
  "name",
  "lastName",
  "email",
  "password",
  "confirmPassword",
]);

function safeText(value = "") {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function normalizeCode(value = "") {
  const code = safeText(value).toUpperCase();

  if (code === "DUPLICATE_EMAIL") return "EMAIL_ALREADY_REGISTERED";

  return REGISTRATION_ERROR_MESSAGES[code] ? code : "";
}

function getStatusCode(error = {}) {
  return Number(error?.statusCode || error?.response?.status || 0);
}

function getRetryAfterSeconds(error = {}) {
  const seconds = Number(
    error?.retryAfterSeconds ||
      error?.response?.data?.retryAfterSeconds ||
      error?.response?.headers?.["retry-after"] ||
      0,
  );

  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
}

function messageForCode(code, fallback = "") {
  return (
    REGISTRATION_ERROR_MESSAGES[code] ||
    getSafeUserMessage(fallback, REGISTRATION_ERROR_MESSAGES.SERVER_ERROR)
  );
}

function normalizeFieldErrors(value = {}) {
  const output = {};

  if (Array.isArray(value)) {
    value.forEach((item) => {
      const field = safeText(item?.field);
      const code = normalizeCode(item?.code);
      const message = getSafeUserMessage(
        item?.message,
        messageForCode(code || "INVALID_REGISTRATION_DATA"),
      );

      if (SAFE_FIELDS.has(field) && message) output[field] = message;
    });

    return output;
  }

  Object.entries(value || {}).forEach(([field, message]) => {
    const safeField = safeText(field);
    const safeMessage = getSafeUserMessage(
      message,
      REGISTRATION_ERROR_MESSAGES.INVALID_REGISTRATION_DATA,
    );

    if (SAFE_FIELDS.has(safeField) && safeMessage) {
      output[safeField] = safeMessage;
    }
  });

  return output;
}

function scopeFor(code, fieldErrors = {}) {
  if (DELIVERY_CODES.has(code)) return "delivery";
  if (
    FIELD_LEVEL_CODES.has(code) &&
    Object.keys(fieldErrors || {}).length > 0
  ) {
    return "field";
  }

  return "form";
}

export function buildClientRegistrationError(code, overrides = {}) {
  const safeCode = normalizeCode(code) || "INVALID_REGISTRATION_DATA";
  const field = safeText(overrides.field || FIELD_BY_CODE[safeCode] || "");
  const message = messageForCode(safeCode, overrides.message);
  const fieldErrors = normalizeFieldErrors(
    overrides.fieldErrors ||
      (field
        ? {
            [field]: message,
          }
        : {}),
  );

  return {
    code: safeCode,
    message,
    field,
    fieldErrors,
    scope: scopeFor(safeCode, fieldErrors),
    verificationRequired: Boolean(overrides.verificationRequired),
    email: safeText(overrides.email),
    statusCode: Number(overrides.statusCode || 0),
    retryAfterSeconds: Number(overrides.retryAfterSeconds || 0),
  };
}

export function normalizeRegistrationError(error) {
  const responseData = error?.response?.data || {};
  const statusCode = getStatusCode(error);
  const isNetworkError =
    error?.message === "Network Error" || (error?.request && !error?.response);
  const responseCode = normalizeCode(responseData.code || error?.code);
  const code =
    responseCode ||
    (isNetworkError
      ? "NETWORK_ERROR"
      : statusCode === 429
        ? "RATE_LIMITED"
        : statusCode >= 500
          ? "SERVER_ERROR"
          : "INVALID_REGISTRATION_DATA");
  const fieldErrors = normalizeFieldErrors(
    responseData.fieldErrors || responseData.errors || {},
  );
  const field = safeText(responseData.field || FIELD_BY_CODE[code] || "");
  const message = messageForCode(code, responseData.message);
  const derivedFieldErrors =
    Object.keys(fieldErrors).length || !field
      ? fieldErrors
      : normalizeFieldErrors({ [field]: message });

  return {
    code,
    message,
    field,
    fieldErrors: derivedFieldErrors,
    scope: scopeFor(code, derivedFieldErrors),
    verificationRequired: Boolean(responseData.verificationRequired),
    email: safeText(responseData.email),
    statusCode,
    retryAfterSeconds: getRetryAfterSeconds(error),
  };
}
